import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Film, ArrowRight, Calendar, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

// Cloudinary images
const IMAGES = {
  hero: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/469821196_122132213570499699_8876230410064543970_n_mevj6e.jpg",
  aprokashito1: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/469821196_122132213570499699_8876230410064543970_n_mevj6e.jpg",
  aprokashito2: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512904/469854315_122132242076499699_6419784136414131561_n_lq0k9o.jpg",
  aprokashito3: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512904/469813353_122132253416499699_89532103118283627_n_ana8w4.jpg",
  gallery1: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/470077372_122132689964499699_3067111797997121261_n_f6uldy.jpg",
  gallery2: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/468335147_122129000006499699_5583324214181612175_n_rii3ua.jpg",
  gallery3: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470221097_122133958580499699_7036109010119657223_n_hpmlcv.jpg",
  premiere1: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470266213_122132673044499699_4752606058197197552_n_x012lo.jpg",
  premiere2: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470261869_122132666054499699_6053191376983885333_n_rvphve.jpg",
  premiere3: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470124006_122132676740499699_5677850631442857850_n_ibqlbe.jpg",
  audience: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512908/469915718_122132673578499699_4338502581283027790_n_g70az7.jpg",
};

const Home = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />

        {/* Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center md:text-left"
        >
          <motion.span
            variants={itemVariants}
            className="inline-block px-4 py-1 bg-[#E50914]/20 border border-[#E50914]/50 rounded-full text-[#E50914] text-sm tracking-widest uppercase mb-6"
          >
            Hindi Cinema Excellence
          </motion.span>

          <motion.h1
            variants={itemVariants}
            className="font-heading text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-white tracking-tight leading-none mb-6"
          >
            SWARNALI
            <span className="block text-gold-gradient">ENTERTAINMENT</span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-lg md:text-xl text-[#A3A3A3] max-w-2xl mb-8 font-light leading-relaxed"
          >
            Crafting compelling narratives that push the boundaries of Hindi cinema. 
            From suspense thrillers to heartfelt dramas, we bring stories to life.
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
            <Link to="/films">
              <Button
                data-testid="explore-films-btn"
                className="bg-[#E50914] hover:bg-[#B20710] text-white px-8 py-6 text-sm tracking-widest uppercase font-semibold rounded-sm"
              >
                <Film className="mr-2" size={18} />
                Explore Our Films
              </Button>
            </Link>
            <a
              href="https://youtu.be/eus3JnJAz3g"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                data-testid="watch-trailer-btn"
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-6 text-sm tracking-widest uppercase font-semibold rounded-sm"
              >
                <Play className="mr-2" size={18} />
                Watch Trailer
              </Button>
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-[#E50914]"
            />
          </div>
        </motion.div>
      </section>

      {/* Featured Film - Aprokashito */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Now Streaming</span>
            <h2 className="font-heading text-5xl md:text-7xl text-white tracking-wide mt-4">
              APROKASHITO
            </h2>
            <p className="text-[#D4AF37] font-accent text-xl italic mt-2">A Suspense Thriller</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Film Poster / Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[3/4] rounded-lg overflow-hidden film-card red-glow">
                <img
                  src={IMAGES.aprokashito1}
                  alt="Aprokashito Film Poster"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>
              {/* Play Button Overlay */}
              <a
                href="https://youtu.be/eus3JnJAz3g"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 flex items-center justify-center group"
              >
                <div className="w-20 h-20 rounded-full bg-[#E50914]/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="text-white ml-1" size={32} fill="white" />
                </div>
              </a>
            </motion.div>

            {/* Film Details */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex flex-wrap gap-4 mb-6">
                <span className="px-3 py-1 bg-[#E50914]/20 text-[#E50914] text-xs tracking-wider uppercase rounded">
                  Suspense
                </span>
                <span className="px-3 py-1 bg-[#E50914]/20 text-[#E50914] text-xs tracking-wider uppercase rounded">
                  Thriller
                </span>
                <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs tracking-wider uppercase rounded">
                  Bengali
                </span>
              </div>

              <h3 className="font-heading text-3xl md:text-4xl text-white mb-4">
                THE UNTOLD STORY OF CRIME AND CREATION
              </h3>

              <p className="text-[#A3A3A3] leading-relaxed mb-6">
                A renowned author's life spirals into darkness when accusations of plagiarism and murder surface. 
                <span className="font-accent italic text-[#D4AF37]"> Aprokashito</span> unravels the complex 
                psychology behind crime and literary creation, leaving audiences questioning the thin line between 
                fiction and reality.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <Calendar className="text-[#D4AF37]" size={20} />
                  <div>
                    <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Release Date</p>
                    <p className="text-white font-semibold">September 6, 2024</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="text-[#D4AF37]" size={20} />
                  <div>
                    <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Director</p>
                    <p className="text-white font-semibold">Soham Acharya</p>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-3">Starring</p>
                <div className="flex flex-wrap gap-2">
                  {['Aryann Bhowmik', 'Joy Sengupta', 'Debleena Dutt'].map((actor) => (
                    <span key={actor} className="px-4 py-2 bg-[#121212] border border-white/10 rounded text-white text-sm">
                      {actor}
                    </span>
                  ))}
                </div>
              </div>

              <Link to="/films">
                <Button
                  data-testid="view-details-btn"
                  className="bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-6 text-sm tracking-widest uppercase font-semibold rounded-sm"
                >
                  View Full Details
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trailer Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Watch Now</span>
            <h2 className="font-heading text-4xl md:text-6xl text-white tracking-wide mt-4">
              OFFICIAL TRAILER
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative max-w-4xl mx-auto rounded-lg overflow-hidden red-glow"
          >
            <div className="video-container">
              <iframe
                src="https://www.youtube.com/embed/eus3JnJAz3g"
                title="Aprokashito Official Trailer"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coming Soon - 2026 Hindi Film */}
      <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
        {/* Background Glow */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-96 h-96 bg-[#E50914]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-full text-[#D4AF37] text-sm tracking-widest uppercase mb-6 pulse-glow">
              Coming Soon
            </span>

            <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl text-white tracking-tight mb-6">
              NEW HINDI FILM
              <span className="block text-gold-gradient">2026</span>
            </h2>

            <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto mb-8">
              Swarnali Entertainment ventures into Hindi cinema with an ambitious new project. 
              Stay tuned for the official announcement.
            </p>

            <div className="flex justify-center gap-4">
              <Link to="/contact">
                <Button
                  data-testid="stay-updated-btn"
                  className="bg-[#E50914] hover:bg-[#B20710] text-white px-8 py-6 text-sm tracking-widest uppercase font-semibold rounded-sm"
                >
                  Stay Updated
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Behind The Scenes</span>
            <h2 className="font-heading text-4xl md:text-6xl text-white tracking-wide mt-4">
              PREMIERE & MOMENTS
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[IMAGES.premiere1, IMAGES.premiere2, IMAGES.premiere3, IMAGES.audience].map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className={`relative overflow-hidden rounded-lg film-card ${
                  index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                }`}
              >
                <img
                  src={img}
                  alt={`Premiere moment ${index + 1}`}
                  className="w-full h-full object-cover aspect-square"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-white text-sm font-medium">Aprokashito Premiere</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-[#121212] to-[#1a1a1a] rounded-2xl p-12 md:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E50914]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />

            <div className="relative z-10">
              <Award className="text-[#D4AF37] mx-auto mb-6" size={48} />
              <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mb-4">
                ASPIRING ACTORS?
              </h2>
              <p className="text-[#A3A3A3] text-lg max-w-xl mx-auto mb-8">
                We're always looking for fresh talent. Check our current audition openings 
                and take your first step into Hindi cinema.
              </p>
              <Link to="/auditions">
                <Button
                  data-testid="view-auditions-btn"
                  className="bg-[#D4AF37] hover:bg-[#AA8C2C] text-black px-8 py-6 text-sm tracking-widest uppercase font-semibold rounded-sm"
                >
                  View Audition Calls
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
